'use client';

import { Button } from '@florencelabs/button';
import { MaterialSymbol, MaterialSymbolName } from '@florencelabs/icons';
import { ModalCloseButton, DialogTitle } from '@florencelabs/modal-2';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export interface SchedulingErrorTemplateProps {
  title: string;
  code?: string;
  body: string | React.ReactNode;
  children?: React.ReactNode;
  icon?: MaterialSymbolName;
}

export const ModalErrorTemplate = ({
  code,
  title,
  body,
  children,
  icon,
}: SchedulingErrorTemplateProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <div
      className={
        'mx-auto flex w-full flex-col rounded-b-lg px-6 pb-16 pt-10 tablet:px-16 desktop:px-12'
      }
    >
      <div className={'flex flex-row justify-end'}>
        <ModalCloseButton className={'static'} ariaLabel={t('close')} />
      </div>
      {icon && (
        <div className={'flex w-full pb-6'}>
          <MaterialSymbol
            size={40}
            name={icon}
            className={'text-primary-950'}
          />
        </div>
      )}
      <div className={'flex w-full flex-col gap-3 pb-10'}>
        {code && <p className={'text-body-small-bold'}>{code.toUpperCase()}</p>}
        <DialogTitle
          className={'font-serif text-display-xlarge'}
          data-testid={'error-title-modal'}
        >
          {title}
        </DialogTitle>
        {typeof body === 'string' ? (
          <p className={'text-display-xsmall'}>{body}</p>
        ) : (
          body
        )}
      </div>
      {children}
    </div>
  );
};

export const GenericDecisionTreeError = (): ReactElement => {
  const { t } = useTranslation();

  return (
    <ModalErrorTemplate
      icon={'error_fill'}
      title={t('decisionTree.missingInformationError.title')}
      body={t('decisionTree.missingInformationError.body')}
    >
      <div className={'flex flex-col gap-2 tablet:flex-row'}>
        <Button
          testId={'back-to-search-btn'}
          slot={'close'}
          variant={'clientMain'}
          className={'py-[10px]'}
        >
          {t('schedule.backToSearch')}
        </Button>
      </div>
    </ModalErrorTemplate>
  );
};
